import { useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { Add } from '@mui/icons-material'
import { Button, Card, DeleteIcon, EditIcon, Table } from 'components'
import { TableActionConfig, TableHeaderType } from 'components/Table/types'
import { usePageHeader, usePagination } from 'utils/hooks'
import { successToast } from 'utils/toastService'
import { deviceTypeTableColumns } from './deviceTypeColumns'
import useAddEditDeviceTypeModal from './dialogs/useAddEditDeviceTypeModal'
import { useInstrumentTypesService } from 'services/instrumentsService'
import { TInstrumentType, TInstrumentTypeWithId } from 'types/models'
import { useDeleteModal } from '../../../../components/dialogs'

export const DeviceTypes = () => {
  const queryClient = useQueryClient()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))

  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } =
    usePagination({ tableKey: 'device-types' })

  const {
    getInstrumentTypes,
    createInstrumentType,
    updateInstrumentType,
    deleteInstrumentType,
  } = useInstrumentTypesService()

  const { data: instrumentTypesData, isLoading } = useQuery({
    queryKey: ['instrument_types_list', currentPage, rowsPerPage],
    queryFn: () =>
      getInstrumentTypes({
        currentPage,
        itemsPerPage: rowsPerPage,
      }),
    retry: false,
  })

  const createInstrumentTypeMutation = useMutation(createInstrumentType, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instrument_types_list'])
      successToast('Device type created')
    },
    onError: (err: { detail?: string }) => {
      toast.error(err?.detail ?? 'Failed to create instrument type.')
      console.error('Failed to create instrument type:', err)
    },
  })

  const updateInstrumentTypeMutation = useMutation(updateInstrumentType, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instrument_types_list'])
      successToast('Device type updated')
    },
    onError: (err: { detail?: string }) => {
      toast.error(err?.detail ?? 'Failed to update instrument type.')
      console.error('Failed to update instrument type:', err)
    },
  })

  const deleteInstrumentTypeMutation = useMutation(deleteInstrumentType, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['instrument_types_list'])
      successToast('Device type deleted')
    },
    onError: (err: { detail?: string }) => {
      toast.error(err?.detail ?? 'Failed to delete instrument type.')
      console.error('Failed to delete instrument type:', err)
    },
  })

  const [addEditModal, toggleAddEditModal] = useAddEditDeviceTypeModal({
    onConfirm: async (newData) => {
      if ((newData as TInstrumentTypeWithId).id) {
        await updateInstrumentTypeMutation.mutateAsync(
          newData as TInstrumentTypeWithId
        )
      } else {
        await createInstrumentTypeMutation.mutateAsync(
          newData as TInstrumentType
        )
      }
    },
  })

  const deviceTypeIdRef = useRef<number | null>(null)

  const [deleteModal, toggleDeleteModal] = useDeleteModal({
    children: 'Are you sure you want to delete this device type?',
    onConfirm: () => {
      if (deviceTypeIdRef.current !== null) {
        deleteInstrumentTypeMutation.mutate(deviceTypeIdRef.current)
      }
    },
  })

  usePageHeader({
    headerTitle: 'Device Types',
    actionButton: (
      <Button startIcon={<Add />} onClick={() => toggleAddEditModal()}>
        Add Device Type
      </Button>
    ),
  })

  const onPaginationChange = (page: number, perPage: number) => {
    setCurrentPage(page)
    setRowsPerPage(perPage)
  }

  const actions: TableActionConfig<TInstrumentTypeWithId>[] = [
    {
      name: 'edit',
      icon: <EditIcon size={16} />,
      handler: (row) => {
        toggleAddEditModal(row)
      },
    },
    {
      name: 'delete',
      icon: <DeleteIcon size={16} />,
      handler: (row) => {
        deviceTypeIdRef.current = row.id
        toggleDeleteModal()
      },
    },
  ]

  return (
    <Box>
      <Card>
        <Box
          sx={{
            height: isSmallScreen ? 'unset' : 'calc(100vh - 120px)',
          }}
        >
          <Table
            data={instrumentTypesData?.results || []}
            columns={deviceTypeTableColumns({
              actions,
            })}
            headerType={TableHeaderType.Card}
            showPagination={true}
            onPaginationChange={onPaginationChange}
            total={instrumentTypesData?.pagination?.total || 0}
            tableKey="device-types"
            isLoading={isLoading}
          />
        </Box>
      </Card>
      {addEditModal}
      {deleteModal}
    </Box>
  )
}
